import React, { useState, useEffect, useReducer, useContext } from 'react';

import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import DatePickerField from 'components/DatePickerField';
import ToIdsComponent from 'components/MeshList/ToIdsComponent';
import currentTaskReducer, { getUpdateFuctions } from 'components/currentTaskReducer';
import PostField from 'components/feed/PostField';
import TextParseComponent from 'components/feed/TextParseComponent';
import useSubObjectiveState from 'components/hooks/useCompanyGoalState';
import { useDispatch } from 'react-redux';
import { useLocation, withRouter } from 'react-router-dom';
import ParentPostLabel from 'screens/common/parentPostLabel';
import DueDateField from 'screens/common/sidepanel/DetailSection/DueDateField';
import ObjectiveTreeView, { ObjectiveDisplay, ObjectiveDisplayContext } from 'screens/home/tasksList/ObjectiveTreeView';
import { CheckRadio } from 'screens/home/tasksList/styles';
import styled, { css } from 'styled-components';
import { StyledTypography, ColoredCircularProgress, NewStyledIconButton } from 'styles/common';

import {
  CREATE_GOAL_TASK_PLACEHOLDER,
  CREATE_TASK_PLACEHOLDER,
  SEARCH_TAG_TYPES,
  TASK_TYPES_2,
} from 'constants/ProjectConstants';
import { getPageName, getTagDetailRoute } from 'constants/helper';
import { AddPeerIcon, StyledIcon, UncheckedCircle } from 'constants/icons';
import { sendTaskEvent, TASK_EVENTS } from 'constants/mixpanelHelper';
import { routesForTracking } from 'constants/routesForTracking';
import theme from 'constants/theme';

import { showCreateSubObjectiveModal } from 'services/createSubObjectiveModal';
import { makeToEmployeesSearchCompatible } from 'services/helpers/employee';
import { doesEditorStateContainContent, getApiBodyFromEditorStateAndMedia } from 'services/helpers/post';
import {
  getCommaSeparatedTeamNames,
  getTags,
  getTeamOptions,
  selectCollaborators,
  selectOwners,
} from 'services/helpers/task';
import { searchAutoComplete } from 'services/search';
import { showErrorSnackbar } from 'services/snackbar';

import { StyledTableRow, StyledTableCell } from './styles';

const editorStyles = css`
  border: 1px solid ${(props) => props.theme.palette.borders.lightGrey};
  min-height: inherit;
  background: white;
  font-size: ${(props) => props.theme.typography.body2.fontSize};
  max-width: 100%;
  // margin-left: -0.5rem;
  padding: ${(props) => props.theme.spacing(0.75)}rem 0.5rem;
  border-radius: 4px;
  &:hover {
    cursor: text;
    border-color: ${(props) => props.theme.palette.borders.darkGrey};
  }
  &:focus-within {
    border-color: ${(props) => props.theme.palette.primary.dark};
  }
`;

const editorPlaceholderStyles = css`
  position: block;
  margin: 0.5rem 0.75rem;
  font-size: ${(props) => props.theme.typography.body2.fontSize};
`;

function makeTaskEmployeeSearchCompatible(task) {
  const toIds = makeToEmployeesSearchCompatible(task.toIds);
  return { ...task, toIds };
}

function getAndSetTeamOptions(toIds, setTeamOptions, updateTeamId) {
  const teamOptions = getTeamOptions(toIds);
  setTeamOptions(teamOptions);
  updateTeamId(teamOptions[0] ? teamOptions[0].id : null);
}

function addCurrentTeamToTaskIfTeamPage(task, tagType, tagId) {
  if (task.teamId) return task;
  if (SEARCH_TAG_TYPES.team === tagType) return { ...task, teamId: tagId };
  return task;
}

function ObjectiveDisplayName() {
  const { selectedObjective } = useContext(ObjectiveDisplayContext);
  return (
    <StyledTypography color="textSecondary" display="inline" variant="body2" noWrap>
      {selectedObjective.name}
    </StyledTypography>
  );
}

// TODO: don't make employee search compatible everytime, only one time is okay
function TaskRowEditView1({
  task,
  taskId,
  tasksDispatch,
  updateTaskInReducer,
  updateTask,
  removeTaskInReducer,
  tagType, // tagType tells what kind of page this is, ORGTAG in case of tagDetail page
  tagId, // this will contain the current team id, when tagType is team otherwise tagId when tagType is hashtag
  userTeams, // teams of loggedInEmployee
  type,
  employee,
  history,
}) {
  const location = useLocation();
  const teamMenuId = `${task.id || task.key}taskTeamDropdown`;
  const taskTeamId = task.teamId;
  const [currentTask, currentTaskDispatch] = useReducer(
    currentTaskReducer,
    addCurrentTeamToTaskIfTeamPage(makeTaskEmployeeSearchCompatible(task), tagType, tagId)
  );
  const [isDatePickerOpen, setDatePickerOpenStatus] = useState(false);
  // if the task is assign to someone, then use his teams, otherwise empty
  const [teamOptions, setTeamOptions] = useState(getTeamOptions(currentTask.toIds));
  // We get current team id from the task otherwise first team of the employee
  const {
    updateDueDate,
    updateTeamId,
    updateToIds,
    updateEditorState,
    updateTask: updateCurrentTask,
    updateCompletionDate,
    addTo,
    removeTo,
  } = getUpdateFuctions(currentTaskDispatch);
  const { teamId: currentTeamId } = currentTask;
  const { subObjective } = task;
  const { selectedSubObjective, subObjectiveColor, subObjectiveIconColor, setSelectedSubObjective } =
    useSubObjectiveState(subObjective);
  const dispatch = useDispatch();

  useEffect(() => {
    if (taskTeamId) updateTeamId(taskTeamId);
  }, [taskTeamId]);

  function openDateTimePicker() {
    setDatePickerOpenStatus(true);
  }

  function closeDateTimePicker() {
    setDatePickerOpenStatus(false);
  }

  function onTeamChange(newTeamId) {
    updateTeamId(newTeamId);
  }

  const firstTag = task.getFirstTag();
  function onTagClick() {
    if (firstTag) {
      history.push(
        getTagDetailRoute({
          tagId: firstTag.uuid,
          tagName: firstTag.name.replace('#', ''),
          tagType: firstTag.tagType,
        })
      );
    }
  }

  const owners = selectOwners(currentTask?.toIds || []);
  const collaborators = selectCollaborators(currentTask?.toIds || []);

  function onOwnerAndCollaboratorChange(updatedEmployees = []) {
    const taskFieldsObjectToUpdate = { toIds: updatedEmployees };
    updateCurrentTask(taskFieldsObjectToUpdate);
    if (tagType !== SEARCH_TAG_TYPES.team) getAndSetTeamOptions(updatedEmployees, setTeamOptions, updateTeamId);
  }

  function stopEditing() {
    // since we are closing the editing state by cross, we need to restore the task back to its previous state
    // which we will get from props
    const oldTask = { ...task, isEditing: false };
    updateCurrentTask(oldTask);
    const hasText = doesEditorStateContainContent({ editorState: oldTask.editorState });
    // if there is no task and no id, we need to remove the task from reducer
    if (!hasText && !taskId) {
      removeTaskInReducer({ taskId, key: currentTask.key });
    } else {
      // updating the task to its old state in reducer
      updateTaskInReducer({ taskId, newTask: oldTask });
    }
  }
  function onCheckChange() {
    const isChecked = Boolean(currentTask.completionDate);
    if (!isChecked) {
      updateCompletionDate(new Date());
    } else {
      updateCompletionDate(null);
    }
  }

  function renderEmployeeNameColumn() {
    let teamId = tagType === SEARCH_TAG_TYPES.team ? tagId : null;
    teamId = isGoalsView ? null : teamId;
    const employees = [...owners, ...collaborators];
    return (
      <EmployeeSearchContainer>
        <ToIdsComponent
          leftGutter={-0.75}
          employees={employees}
          onUpdate={onOwnerAndCollaboratorChange}
          iconSize="fixed32px"
          maxNodes={3}
          renderEmptyView={(props) => (
            <AddPeerIcon
              {...props}
              color="white"
              circular
              size="fixed28px"
              style={{ marginLeft: employees.length ? '-6px' : '0px' }}
            />
          )}
        />
      </EmployeeSearchContainer>
    );
  }

  function updateTaskFn() {
    if (type === TASK_TYPES_2.goal && !selectedSubObjective) {
      dispatch(showErrorSnackbar('Please select an objective'));
      return;
    }

    if (!taskId) {
      // need to check if it from hashTag screen or team Screen.

      const eventObject = {
        page: {
          pageName: routesForTracking.team.pageName,
          flow: 'Activity',
        },
        eventName: TASK_EVENTS.addedNew,
        referrer: 'List',
      };

      const pageName = getPageName(location);

      switch (pageName) {
        case routesForTracking.team.pageName:
          break;
        case routesForTracking.tagDetail.pageName:
          eventObject.page.pageName = routesForTracking.tagDetail.pageName;
          break;
        default:
          eventObject.page.pageName = null;
      }

      sendTaskEvent(eventObject);
    }

    updateTask({ taskId, newTask: { ...currentTask, subObjective: selectedSubObjective } });
  }

  function onRemoveMention({ removedMentionEntity, mentionsPresent }) {
    removeTo(removedMentionEntity.data.mention.employee.id);
    if (tagType !== SEARCH_TAG_TYPES.team) getAndSetTeamOptions(currentTask.toIds, setTeamOptions, updateTeamId);
  }

  function onAddMention(mention) {
    const employee = makeToEmployeesSearchCompatible([mention.employee])[0];
    // TODO: remove the mention here and then update to and editorState seperately because the user would first like to see
    // what impact his mention had and then we can show that yeah we removed the mention
    addTo(employee);
    if (tagType !== SEARCH_TAG_TYPES.team) getAndSetTeamOptions(currentTask.toIds, setTeamOptions, updateTeamId);
  }

  //
  //  todo - refactor: move this component to a separate file
  //
  const [employeeIds, setEmployeeIds] = useState([]);
  const [selectedObjectiveIds, setSelectedObjectiveIds] = useState([]);
  const [defaultOptions, setDefaultOptions] = useState([]);

  // update the subobjectivelist
  const updateSubObjectivesList = (forceFetch) => {
    if (task.isEditing) {
      const { macros } = getApiBodyFromEditorStateAndMedia(currentTask.editorState);
      // set employeeIds as: the tagged employee Ids or the current employee
      const eIds =
        currentTask.toIds && currentTask.toIds.length ? currentTask.toIds.map((toId) => toId.value) : [employee.id];
      // set objectiveIds as: GOAL type tags selected
      const oIds = Object.keys(macros.attributes.orgTag).filter(
        (tag) => macros.attributes.orgTag[tag].tagType === 'GOAL'
      );
      // forceFetch - after a task/goal is focussed, to handle the case when a new sub objective is created from the popup
      // else, check if any new employee / 'GOAL' type tag has been mentioned in the task
      if (
        forceFetch ||
        employeeIds.sort((a, b) => a - b).toString() !== eIds.sort((a, b) => a - b).toString() ||
        selectedObjectiveIds.sort((a, b) => a.localeCompare(b)).toString() !==
          oIds.sort((a, b) => a.localeCompare(b)).toString()
      ) {
        setEmployeeIds(eIds);
        setSelectedObjectiveIds(oIds);
        const callback = function (res) {
          const options =
            type === TASK_TYPES_2.goal
              ? [
                  {
                    value: '-1',
                    label: '+ Create New',
                  },
                  ...res,
                ]
              : res;
          setDefaultOptions(options);
        };
        searchAutoComplete({
          query: '',
          tagType: SEARCH_TAG_TYPES.subObjectiveTag,
          employeeId: eIds,
          selectedObjectiveId: oIds,
          selectedSubObjectiveId: selectedSubObjective && selectedSubObjective.id,
          widgetType: type,
          callback,
        });
      }
    }
  };

  useEffect(() => {
    updateSubObjectivesList(false);
  }, [JSON.stringify(currentTask.editorState), JSON.stringify(currentTask.toIds), task.isEditing]);

  const renderObjectivesDropdown = () => {
    const onSubObjectiveChange = (subObjective) => {
      if (subObjective.value === '-1') {
        dispatch(showCreateSubObjectiveModal());
      } else {
        setSelectedSubObjective(subObjective);
      }
    };

    return (
      <ObjectiveTreeView
        selectedObjective={selectedSubObjective}
        objectiveColor={subObjectiveColor}
        objectiveIconColor={subObjectiveIconColor}
        onObjectiveChange={onSubObjectiveChange}
        renderObjectiveDisplay={() => (
          <StyledObjectiveDisplay>
            <ObjectiveDisplayName />
          </StyledObjectiveDisplay>
        )}
      />
    );
  };

  function renderActions() {
    return (
      <>
        <NewStyledIconButton onClick={updateTaskFn} padding="small" circular backgroundColor="white">
          <StyledIcon size="tinier" color="primary">
            <CheckIcon />
          </StyledIcon>
        </NewStyledIconButton>
        <NewStyledIconButton padding="small" onClick={stopEditing} circular backgroundColor="white">
          <StyledIcon size="tinier" color="red">
            <CloseIcon />
          </StyledIcon>
        </NewStyledIconButton>
      </>
    );
  }

  function renderGoalsView() {
    return (
      <GoalRow
        isEditing={currentTask.isEditing}
        // to handle the case when a new sub objective is created from the popup,
        onClick={() => updateSubObjectivesList(true)}
      >
        <StyledTableCell border width="35%" paddingtop="1rem" paddingBottom="1rem">
          {currentTask.isEditing ? (
            <PostField
              placeholder={CREATE_GOAL_TASK_PLACEHOLDER}
              editorState={currentTask.editorState}
              updateEditorState={updateEditorState}
              // useMentionFunctionality={tagType !== SEARCH_TAG_TYPES.tag}
              useMentionFunctionality
              editorStyles={editorStyles}
              placeholderStyles={editorPlaceholderStyles}
              onEnter={updateTaskFn}
              // use selected teamId in the dropdown to create a new tag
              teamId={tagType === SEARCH_TAG_TYPES.tag ? null : tagId} // in case of new task, we have to use the teamId on the team page
              onAddMention={onAddMention}
              onRemoveMention={onRemoveMention}
              widgetType={type}
              useHashtagFunctionality={false}
            />
          ) : (
            <TextParseComponent text={currentTask.text} res={currentTask.macros} />
          )}
        </StyledTableCell>
        <StyledTableCell border paddingLeft="0" width="15%">
          {renderObjectivesDropdown()}
        </StyledTableCell>
        <StyledTableCell border width="20%" paddingLeft="16px" paddingtop="0" paddingRight="0.5rem">
          {renderEmployeeNameColumn()}
        </StyledTableCell>
        <StyledTableCell width="25%" />
        <StyledTableCell border width="5%" align="center" paddingRight="2px" textAlign="right">
          <ActionIconsContainer>
            {task.isUpdating ? <ColoredCircularProgress color="textSecondary" size="tiny" /> : renderActions()}
          </ActionIconsContainer>
        </StyledTableCell>
      </GoalRow>
    );
  }

  const isGoalsView = type === TASK_TYPES_2.goal;

  const coloredDueDate = currentTask.coloredDueDate || {};

  return isGoalsView ? (
    renderGoalsView()
  ) : (
    <>
      <TaskRow
        isEditing={currentTask.isEditing}
        // to handle the case when a new sub objective is created from the popup,
        onClick={() => updateSubObjectivesList(true)}
      >
        <StyledTableCell border align="center">
          {!currentTask.isUnassignedTask() && (
            <CheckRadio
              hoverColor="textSecondary"
              onClick={task.isUpdating ? null : onCheckChange}
              disableRipple
              checked={Boolean(currentTask.completionDate)}
              icon={<UncheckedCircle size="header" color="textSecondary" />}
              size="h6"
              checkedIcon={<UncheckedCircle size="header" color="green" />}
            />
          )}
        </StyledTableCell>
        <StyledTableCell border width="33%" paddingLeft="0">
          <PostField
            placeholder={CREATE_TASK_PLACEHOLDER}
            editorState={currentTask.editorState}
            updateEditorState={updateEditorState}
            // useMentionFunctionality={tagType !== SEARCH_TAG_TYPES.tag}
            useMentionFunctionality
            editorStyles={editorStyles}
            placeholderStyles={editorPlaceholderStyles}
            onEnter={updateTaskFn}
            // use selected teamId in the dropdown to create a new tag
            teamId={tagType === SEARCH_TAG_TYPES.tag ? null : tagId} // in case of new task, we have to use the teamId on the team page
            onAddMention={onAddMention}
            onRemoveMention={onRemoveMention}
            widgetType={type}
          />
        </StyledTableCell>
        <StyledTableCell border width="12%" paddingLeft="0">
          <div style={{ maxWidth: '175px' }}>
            <ParentPostLabel parentPosts={task.parentPosts} />
          </div>
        </StyledTableCell>
        <StyledTableCell border width="12%" paddingLeft="0">
          {tagType === SEARCH_TAG_TYPES.team &&
            (getTags(task).length > 0 ? (
              <StyledTypography variant="body2" onClick={onTagClick}>
                {getTags(task)
                  .map((tag) => `#${tag.name}`)
                  .join(', ')}
              </StyledTypography>
            ) : (
              <StyledTypography variant="body2">-</StyledTypography>
            ))}
          {
            // show teams if its the tag page
            tagType === SEARCH_TAG_TYPES.tag && (
              <StyledTypography variant="body2">{getCommaSeparatedTeamNames(task)}</StyledTypography>
            )
          }
        </StyledTableCell>
        <StyledTableCell
          border={false}
          width="16%"
          paddingLeft="0"
          paddingtop="0"
          paddingBottom="10px"
          paddingRight="10px"
        >
          {renderEmployeeNameColumn()}
        </StyledTableCell>
        <StyledTableCell border paddingLeft="1.65rem" width="10%" style={{ whiteSpace: 'nowrap' }}>
          <DueDateField
            dueDate={coloredDueDate.timeStamp ? new Date(coloredDueDate.timeStamp) : null}
            color={coloredDueDate.color}
            tooltipText={coloredDueDate.reason}
            isCrossed={coloredDueDate.isCrossed}
            detailedStatus={currentTask.detailedStatus}
            updateDueDate={(date) =>
              updateDueDate({
                dueDate: date,
                coloredDueDate: {
                  ...coloredDueDate,
                  color: date ? theme.palette.primary.main : theme.palette.grey.dark,
                  reason: '',
                  timeStamp: date ? date.getTime() : null,
                },
              })
            }
          />
        </StyledTableCell>
        <StyledTableCell border align="center" paddingRight="2px" textAlign="right" width="8%">
          <ActionIconsContainer>
            {task.isUpdating ? <ColoredCircularProgress color="textSecondary" size="tiny" /> : <>{renderActions()}</>}
          </ActionIconsContainer>
        </StyledTableCell>
        <StyledTableCell border />
      </TaskRow>
      <DatePickerField
        isOpen={isDatePickerOpen}
        onOpen={openDateTimePicker}
        onClose={closeDateTimePicker}
        onChange={updateDueDate}
      />
    </>
  );
}

export default withRouter(TaskRowEditView1);

const EmployeeSearchContainer = styled.div`
  height: 100%;
  width: 100%;
`;

const ActionIconsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: 0.5rem;
  align-items: center;
  justify-content: center;
`;

const StyledObjectiveDisplay = styled(ObjectiveDisplay)`
  background-color: white;
  margin-left: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${(props) => props.theme.palette.borders.lightGrey};
  &:hover {
    border-color: ${(props) => props.theme.palette.borders.darkGrey};
    background-color: white;
  }
  width: 100%;
  justify-content: flex-start;
`;

const TaskRow = styled(StyledTableRow)`
  td {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
  &:hover {
    ${(props) =>
      props.onClick &&
      css`
        cursor: auto;
      `}
  }
`;

const GoalRow = styled(StyledTableRow)`
  &:hover {
    ${(props) =>
      props.onClick &&
      css`
        cursor: auto;
      `}
  }
`;
